import * as Linking from 'expo-linking';
import ExpoConstants from 'expo-constants';
import {
	getStateFromPath,
	getPathFromState,
	type LinkingOptions,
	type NavigationState,
	type PartialState,
} from '@react-navigation/native';
import Constants from 'src/core/utils/Constants';
import { useEventStore } from 'src/domain/stores/useEventStore';

const TAG = '[DEEPLINKING] ->';
const prefix = Linking.createURL('/');
const baseUrl = (ExpoConstants.expoConfig?.extra?.baseUrl as string | undefined) ?? '';
// Origin of the web build. Without it an https link to the web app itself (e.g.
// https://app.topicusevents.nl/event/ABC) matches no prefix, because baseUrl points at
// the backend host instead.
const webUrl = (ExpoConstants.expoConfig?.extra?.webUrl as string | undefined) ?? '';

// Read the scheme this build actually registered (app.config.ts resolves it per environment: topev,
// topev-test, topev-feat, topev-dev) instead of hardcoding it, so every variant only accepts its own links.
const configuredScheme = Array.isArray(ExpoConstants.expoConfig?.scheme)
	? ExpoConstants.expoConfig?.scheme[0]
	: ExpoConstants.expoConfig?.scheme;
const schemePrefixes = configuredScheme ? [`${configuredScheme}://`] : [];

type RouteParams = Record<string, unknown>;
type AnyState = NavigationState | PartialState<NavigationState>;
// Exactly what getStateFromPath hands back, so building on top of it cannot widen the type the
// linking config promises to return.
type ResolvedState = NonNullable<ReturnType<typeof getStateFromPath>>;
type AnyRoute = ResolvedState['routes'][number];

/**
 * Screens are navigated to with an `eventUuid`, but a URL names the event by its
 * public code, so the code is looked up here rather than at every navigate call.
 *
 * Falls back to the uuid when the event is not in the store yet (a link built
 * before the event list loaded). Such a URL still resolves, because the screens
 * accept a code or a uuid in that segment.
 */
// A password-reset link carries a single-use token. This file logs the incoming path on every
// route resolution, and on web that log lands in a console anyone at the machine can open, so the
// value itself never gets printed.
function withoutToken(path: string): string {
	return path.replace(/([?&]token=)[^&]+/gi, '$1<redacted>');
}

function eventCodeFor(eventUuid: string): string {
	const event = useEventStore.getState().eventData.find((candidate) => candidate.uuid === eventUuid);
	return event?.code ?? eventUuid;
}

/**
 * Which parameters each route's path actually needs, read from the path table itself so the two
 * cannot drift apart. Providing a parameter a route does not use would turn it into a query
 * string; leaving one out that it does use puts the literal text "undefined" in the URL.
 */
function pathParamsByRoute(screens: Record<string, unknown>, into: Map<string, string[]> = new Map()) {
	for (const [name, value] of Object.entries(screens)) {
		const path = typeof value === 'string' ? value : (value as { path?: string }).path;
		const nested = typeof value === 'string' ? undefined : (value as { screens?: Record<string, unknown> }).screens;

		if (path !== undefined) {
			into.set(
				name,
				[...path.matchAll(/:([A-Za-z]+)/g)].map((match) => match[1] as string),
			);
		}

		if (nested) pathParamsByRoute(nested, into);
	}

	return into;
}

/**
 * The parameters this route needs in its path, filled from whatever the app handed over.
 *
 * In the app a screen is navigated to with the whole page object and often no ids at all: that is
 * its own contract and it works, because the receiving screen reads the object. A URL cannot carry
 * an object, so those ids have to come back out of it -- every page carries its own `id` and the
 * `event_uuid` of the event it belongs to. Without this the address bar showed
 * `/evenement/undefined/pagina/undefined`, and a link like that is worthless to whoever receives it.
 */
let neededByRoute: Map<string, string[]> | null = null;

function neededParams(routeName: string): string[] {
	// Built on first use: the path table is declared further down in this file.
	neededByRoute ??= pathParamsByRoute(LinkingConfiguration.config?.screens ?? {});

	return neededByRoute.get(routeName) ?? [];
}

function urlParams(routeName: string, params: RouteParams | undefined): RouteParams | undefined {
	const needed = neededParams(routeName);
	if (needed.length === 0 && params?.token === undefined) return undefined;

	// Two shapes reach here. `navigate(screen, { data: page })` is the common one, but the Foto's
	// tab opens an album with `navigate(screen, album)` -- the object itself as the params. Reading
	// only `data` left the album's id nowhere to be found, so the path became /fotos/undefined.
	const page = (params?.data ?? params) as { id?: number | string; event_uuid?: string } | undefined;
	const eventUuid =
		(typeof params?.eventUuid === 'string' ? params.eventUuid : undefined) ??
		(typeof page?.event_uuid === 'string' ? page.event_uuid : undefined);

	const kept: RouteParams = {};

	for (const key of needed) {
		if (params?.[key] !== undefined) {
			kept[key] = params[key];
			continue;
		}

		if (key === 'code') {
			if (eventUuid !== undefined) kept.code = eventCodeFor(eventUuid);
			continue;
		}

		// pageId, dayId, galleryId: all of them identify the page this screen is showing.
		if (page?.id !== undefined) kept[key] = page.id;
	}

	// The reset token is not part of any path pattern; it travels as a query and the drawer strips
	// it again before the host ever sees it.
	if (params?.token !== undefined) kept.token = params.token;

	return Object.keys(kept).length > 0 ? kept : undefined;
}

/** Rewrites a navigation state so it serialises into a URL instead of a param dump. */
function stateForUrl(state: AnyState): AnyState {
	return {
		...state,
		routes: (state.routes ?? []).map((route) => ({
			...route,
			params: urlParams(route.name, route.params as RouteParams | undefined),
			state: route.state ? stateForUrl(route.state) : undefined,
		})),
	} as AnyState;
}

/** Drops the deepest route, so a path that cannot be completed falls back to its parent. */
function withoutDeepestRoute(state: AnyState): AnyState | null {
	const routes = state.routes ?? [];
	if (routes.length === 0) return null;

	const last = routes[routes.length - 1];

	if (last?.state) {
		const trimmed = withoutDeepestRoute(last.state);

		if (trimmed) {
			return { ...state, routes: [...routes.slice(0, -1), { ...last, state: trimmed }] } as AnyState;
		}
	}

	if (routes.length === 1) return null;

	// The index has to shrink along with the routes: it usually points at the route being dropped,
	// and React Navigation reads `routes[index]` without checking, so a stale index crashes there.
	const remaining = routes.slice(0, -1);

	return { ...state, index: remaining.length - 1, routes: remaining } as AnyState;
}

/**
 * The declared paths this one sits under, outermost first.
 *
 * Read from the path table by asking React Navigation itself, so the two cannot drift apart: a
 * prefix that resolves to a single root-level screen is a step of its own, a prefix that resolves
 * to nothing (`evenement`, or `evenement/:code/pagina`) is not a destination and is skipped, and
 * one that resolves into the tabs is the home screen, which the base route already covers.
 */
function ancestorRoutes(path: string, options: Parameters<typeof getStateFromPath>[1]): AnyRoute[] {
	const pathname = path.split('?')[0]?.replace(/^\/+/, '') ?? '';
	const segments = pathname.split('/').filter(Boolean);
	const routes: AnyRoute[] = [];

	for (let depth = 1; depth < segments.length; depth += 1) {
		// With a leading slash, because getStateFromPath stamps the string it is given onto the route
		// as `route.path`, and React Navigation writes the address bar from that in preference to
		// getPathFromState. A relative one is resolved against the URL already showing, which turned
		// /evenement/webtest/programma into /evenement/webtest/evenement/webtest on the way back.
		const ancestor = getStateFromPath(`/${segments.slice(0, depth).join('/')}`, options);
		const route = ancestor?.routes.length === 1 ? (ancestor.routes[0] as AnyRoute | undefined) : undefined;

		if (route && !route.state && route.name !== Constants.screenIndex) {
			routes.push(route);
		}
	}

	return routes;
}

/**
 * The screen the path names, with the tabs and every declared step of the path underneath it.
 *
 * React Navigation resolves a path to that screen and nothing else, so a shared link or a refresh
 * used to arrive on a stack one route deep and the header's arrow -- which calls goBack() -- did
 * nothing at all. On web that arrow is the way back people reach for first, and the only one inside
 * the drawer that stays within the app: React Navigation pushes a history entry per screen, so the
 * browser's back button walks back through those before it leaves. Mounting the steps is safe:
 * EventScreen deliberately does not fetch on mount.
 */
function withBaseStack(
	state: ResolvedState,
	path: string,
	options: Parameters<typeof getStateFromPath>[1],
): ResolvedState {
	const routes = state.routes;
	const target = routes[routes.length - 1];

	// Only a screen that arrived alone at the root. A screen nested in the tabs
	// (`instellingen/feedback`) resolves into Index and needs its parent inside that stack instead,
	// which this does not do.
	if (routes.length !== 1 || !target || target.name === Constants.screenIndex || target.state) {
		return state;
	}

	const routesWithBase = [{ name: Constants.screenIndex }, ...ancestorRoutes(path, options), target];

	// The index has to move with the routes. React Navigation puts `index: 0` on a state with one
	// route, and leaving that in place points it at the tabs instead of the screen the path names --
	// which serialised back as `/?code=webtest` rather than `/evenement/webtest`.
	return { ...state, index: routesWithBase.length - 1, routes: routesWithBase };
}

/**
 * Declarative deep-link config for React Navigation. Routes URLs with this
 * build's own scheme, the Expo-dev prefix, or the environment's HTTPS base URL
 * into the correct screen with typed params.
 *
 * Every path is public once it ships, so the names are Dutch and follow the
 * labels in the app rather than the internal screen names (#385, ROUTE-03).
 *
 * Four screens deliberately have no path: `Splash` and `EventDeeplink`'s sibling
 * `FormClosed` are states rather than destinations, `NewsItemPage` is only
 * reachable from a component nothing renders, and `TimeTablePage` shows a
 * timetable block that the API gives no identity, so a link to it could not
 * survive an edit in the CMS. A shared timetable link stops at the day.
 */
/**
 * decodeURIComponent throws a URIError on a half-written escape (`%E0%A4%A`), and this runs inside
 * route resolution: on web that is before the first paint, so a mangled link in a mail client would
 * show a blank page instead of the fallback ROUTE-05 promises. A token we cannot decode is a token
 * the server will reject anyway; passing it through unchanged gets the visitor to the screen that
 * says so.
 */
function safeDecode(raw: string): string {
	try {
		return decodeURIComponent(raw);
	} catch {
		return raw;
	}
}

/**
 * The screens with no declared path. React Navigation falls back to the route NAME as a segment for
 * those, which publishes an internal CamelCase name (`/TimeTablePage`) that resolves to nothing:
 * refresh or share it and ROUTE-05 drops the visitor on the home screen, event context and all.
 * Only TimeTablePage is actually reachable; the other three are states nothing navigates to.
 */
const pathlessScreens: string[] = [
	Constants.screenSplash,
	Constants.screenFormClosed,
	Constants.screenNewsItemPage,
	Constants.screenTimeTablePage,
];

function namesPathlessScreen(path: string): boolean {
	const segments = path.split('?')[0]?.split('/').filter(Boolean) ?? [];

	return segments.some((segment) => pathlessScreens.includes(segment));
}

const LinkingConfiguration: LinkingOptions<ReactNavigation.RootParamList> = {
	prefixes: [...new Set([prefix, ...schemePrefixes, baseUrl, webUrl].filter(Boolean))],
	config: {
		screens: {
			// The tabs and the two stacks inside them. The nesting has to mirror the
			// navigators exactly: a screen declared at the wrong depth never matches.
			[Constants.screenIndex]: {
				screens: {
					[Constants.screenHome]: '',
					[Constants.screenArchive]: 'archief',
					[Constants.screenMessages]: 'berichten',
					[Constants.screenPhotos]: {
						// Without this a cold load on a nested path builds a stack one route deep and the
						// back arrow does nothing. React Navigation only puts the parent underneath when the
						// linking config says which route that is; the navigator's own initialRouteName does
						// not apply to state that came from a URL.
						//
						// Cast because LinkingOptions checks a nested initialRouteName against the parent's
						// param list rather than the stack's own, so every correct value is rejected here.
						initialRouteName: Constants.screenRootGalleries as never,
						screens: {
							[Constants.screenRootGalleries]: 'fotos',
							[Constants.screenRootGalleryHolder]: 'fotos/:galleryId',
							[Constants.screenRootGalleryPage]: 'fotos/:galleryId/:pageId',
						},
					},
					[Constants.screenSettings]: {
						initialRouteName: Constants.screenSettingsRoot as never,
						screens: {
							[Constants.screenSettingsRoot]: 'instellingen',
							[Constants.screenFeedbackPage]: 'instellingen/feedback',
							[Constants.screenContactPage]: 'instellingen/contact',
							[Constants.screenProfilePage]: 'instellingen/profiel',
							[Constants.screenUpdatePassword]: 'instellingen/wachtwoord',
							[Constants.screenProfileRegistrationPage]: 'registreren',
							[Constants.screenProfileLoginPage]: 'inloggen',
							[Constants.screenLostPasswordPage]: 'wachtwoord-vergeten',
						},
					},
				},
			},

			// Everything below an event. `:code` is the public event code; the screens
			// accept a uuid there too, so a link built before the event list loaded
			// still resolves.
			[Constants.screenEvent]: 'evenement/:code',
			[Constants.screenProgram]: 'evenement/:code/programma',
			[Constants.screenProgramHolder]: 'evenement/:code/programma/:pageId',
			[Constants.screenProgramItem]: 'evenement/:code/programma/:pageId/onderdeel',
			[Constants.screenTimetableHolder]: 'evenement/:code/blokkenschema',
			[Constants.screenTimetableDay]: 'evenement/:code/blokkenschema/:pageId',
			[Constants.screenNews]: 'evenement/:code/nieuws',
			[Constants.screenNewsItem]: 'evenement/:code/nieuws/:pageId',
			[Constants.screenLocations]: 'evenement/:code/locaties',
			[Constants.screenLocationItem]: 'evenement/:code/locaties/:pageId',
			[Constants.screenFood]: 'evenement/:code/eten-en-drinken',
			[Constants.screenFoodItem]: 'evenement/:code/eten-en-drinken/:pageId',
			[Constants.screenFloorplan]: 'evenement/:code/plattegrond',
			[Constants.screenCustom]: 'evenement/:code/pagina/:pageId',
			[Constants.screenGalleryHolder]: 'evenement/:code/fotos',
			[Constants.screenGallery]: 'evenement/:code/fotos/:pageId',
			[Constants.screenFormLogin]: 'evenement/:code/aanmelden',

			// Account and one-off screens.
			[Constants.screenLogin]: 'welkom',
			[Constants.screenLoginForm]: 'inloggen/formulier',
			[Constants.screenRegistrationForm]: 'registreren/formulier',
			[Constants.screenForgotPassword]: 'wachtwoord-vergeten/formulier',
			[Constants.screenQrScanner]: 'scannen',
			[Constants.screenMySubmission]: 'mijn-aanmelding',

			// The QR code on printed material keeps its short English path.
			[Constants.screenEventDeeplink]: 'event/:code',

			[Constants.screenChangePassword]: {
				path: 'wachtwoord-instellen',
				parse: {
					token: safeDecode,
				},
				stringify: {
					token: (value: string) => encodeURIComponent(value ?? ''),
				},
			},
		},
	},
	// Build the full navigation state so a deeplink is triggered ON TOP of the base tabs (Index).
	getStateFromPath: (path, options) => {
		console.info(`${TAG} incoming path:`, withoutToken(path));

		// Password-reset links in the wild carry the token on an empty path
		// (`topev://?token=abc`) or behind a legacy `+password/reset` segment. The
		// empty path now belongs to the home tab, so normalise both onto the screen
		// that handles the token. Event links carry no `token` and stay untouched.
		const resetToken = /[?&]token=([^&]+)/.exec(path);
		const pathname = path.split('?')[0]?.replace(/^\/+/, '') ?? '';
		if (resetToken && (pathname === '' || /password/i.test(pathname))) {
			path = `wachtwoord-instellen?token=${resetToken[1]}`;
		}

		const state = getStateFromPath(path, options);
		if (!state) {
			// A path the app does not know belongs on the home screen, without an
			// error (ROUTE-05). Returning undefined would leave the visitor on
			// whatever the navigator happened to start with.
			console.warn(`${TAG} unknown path, falling back to home:`, withoutToken(path));
			return { routes: [{ name: Constants.screenIndex }] };
		}

		const target = state.routes[state.routes.length - 1];

		// ChangePassword is only a destination with an actual reset token. Without one
		// it is an empty form the visitor cannot do anything with, so send them home
		// (ROUTE-01).
		if (target?.name === Constants.screenChangePassword && !/[?&]token=/.test(path)) {
			console.info(`${TAG} no reset token -> home instead of ChangePassword`);
			return { routes: [{ name: Constants.screenIndex }] };
		}

		if (target?.name === Constants.screenEventDeeplink) {
			const code = (target.params as { code?: string } | undefined)?.code;
			if (!code) {
				// Same fallback as an unknown path above: returning undefined leaves the visitor on
				// whatever the navigator happened to start with.
				console.warn(`${TAG} no event code in path:`, path.split('?')[0]);

				return { routes: [{ name: Constants.screenIndex }] };
			}
			return {
				routes: [{ name: Constants.screenIndex }, { name: Constants.screenEventDeeplink, params: { code } }],
			};
		}

		return withBaseStack(state, path, options);
	},

	// The app navigates with an eventUuid and hands whole page objects along; a URL
	// wants the event code and nothing else. Rewrite the state before serialising it.
	getPathFromState: (state, options) => {
		// A path we cannot complete is not a path worth publishing: React Navigation writes the
		// literal text "undefined" for a missing path parameter, and the drawer would put that in
		// the host's address bar. Fall back to the deepest screen that does resolve.
		let candidate: AnyState | null = state;

		while (candidate) {
			const path = getPathFromState(stateForUrl(candidate) as NavigationState, options);

			if (!path.includes('/undefined') && !namesPathlessScreen(path)) return path;

			candidate = withoutDeepestRoute(candidate);
		}

		return '/';
	},

	async getInitialURL() {
		return (await Linking.getInitialURL()) ?? null;
	},
};

export default LinkingConfiguration;
